<template>
    <div class="home-recommend">
      <div class="page-header">
        <div>
          <h2 class="page-title">首页推荐</h2>
          <p class="page-desc">配置首页推荐内容，支持商品、活动推荐</p>
        </div>
        <el-button type="primary" size="large" @click="openAddDialog">
          <el-icon><Plus /></el-icon>
          新增推荐
        </el-button>
      </div>
  
      <!-- 搜索筛选栏 -->
      <div class="filter-bar">
        <el-input 
          v-model="searchKey" 
          placeholder="搜索区块名称"
          prefix-icon="Search"
          clearable
          style="width: 260px"
          @keyup.enter="fetchData"
        />
        <el-select v-model="sectionTypeFilter" placeholder="全部类型" clearable style="width: 120px" @change="fetchData">
          <el-option label="商品" value="product" />
          <el-option label="活动" value="activity" />
          <el-option label="商户" value="merchant" />
        </el-select>
        <el-select v-model="statusFilter" placeholder="全部状态" clearable style="width: 120px" @change="fetchData">
          <el-option label="显示" :value="1" />
          <el-option label="隐藏" :value="0" />
        </el-select>
        <el-button type="primary" @click="fetchData">查询</el-button>
        <el-button @click="resetSearch">重置</el-button>
      </div>
  
      <!-- 推荐列表 -->
      <div class="recommend-grid">
        <div class="recommend-card" v-for="item in recommendList" :key="item.id">
          <div class="recommend-cover">
            <img :src="item.cover_image || defaultImage" :alt="item.section_name" />
            <div class="recommend-type" :class="getTypeClass(item.section_type)">
              {{ getTypeText(item.section_type) }}
            </div>
          </div>
          <div class="recommend-info">
            <h3 class="section-name">{{ item.section_name }}</h3>
            <div class="section-title">{{ item.section_title || '无标题' }}</div>
            <div class="section-subtitle">{{ item.section_subtitle || '' }}</div>
            <div class="recommend-meta">
              <span>排序: {{ item.sort_order }}</span>
              <el-tag :type="item.status === 1 ? 'success' : 'danger'" size="small">
                {{ item.status === 1 ? '显示' : '隐藏' }}
              </el-tag>
            </div>
          </div>
          <div class="recommend-actions">
            <el-button type="primary" link size="small" @click="openEditDialog(item)">
              <el-icon><Edit /></el-icon> 编辑
            </el-button>
            <el-popconfirm title="确定删除该推荐吗？" @confirm="handleDelete(item.id)">
              <template #reference>
                <el-button type="danger" link size="small">
                  <el-icon><Delete /></el-icon> 删除
                </el-button>
              </template>
            </el-popconfirm>
          </div>
        </div>
  
        <div v-if="recommendList.length === 0 && !loading" class="empty-state">
          <el-empty description="暂无推荐，点击右上角新增" />
        </div>
      </div>
  
      <!-- 分页 -->
      <div class="pagination-wrapper" v-if="pagination.total > pagination.limit">
        <el-pagination
          v-model:current-page="pagination.page"
          v-model:page-size="pagination.limit"
          :total="pagination.total"
          :page-sizes="[6, 12, 18]"
          layout="total, sizes, prev, pager, next"
          @size-change="fetchData"
          @current-change="fetchData"
        />
      </div>
  
      <!-- 新增/编辑弹窗 -->
      <el-dialog v-model="dialogVisible" :title="isEditMode ? '编辑推荐' : '新增推荐'" width="600px" destroy-on-close>
        <el-form :model="formData" :rules="formRules" ref="formRef" label-width="100px">
          <el-form-item label="区块名称" prop="section_name" required>
            <el-input v-model="formData.section_name" placeholder="请输入区块名称，如：限时抢购" />
          </el-form-item>
  
          <el-form-item label="推荐类型" prop="section_type" required>
            <el-select v-model="formData.section_type" placeholder="请选择推荐类型" style="width: 100%" @change="onTypeChange">
              <el-option label="商品推荐" value="product" />
              <el-option label="活动推荐" value="activity" />
              <el-option label="商户推荐" value="merchant" />
            </el-select>
          </el-form-item>
  
          <el-form-item label="区块标题">
            <el-input v-model="formData.section_title" placeholder="请输入区块标题" />
          </el-form-item>
  
          <el-form-item label="区块副标题">
            <el-input v-model="formData.section_subtitle" placeholder="请输入区块副标题" />
          </el-form-item>
  
          <el-form-item label="推荐内容" prop="item_id" required>
            <el-select 
              v-if="formData.section_type === 'product'"
              v-model="formData.item_id" 
              placeholder="请选择商品" 
              filterable
              style="width: 100%"
            >
              <el-option v-for="product in productOptions" :key="product.id" :label="product.product_name" :value="product.id" />
            </el-select>
            <el-select 
              v-else-if="formData.section_type === 'activity'"
              v-model="formData.item_id" 
              placeholder="请选择活动" 
              filterable
              style="width: 100%"
            >
              <el-option v-for="activity in activityOptions" :key="activity.id" :label="activity.activity_name" :value="activity.id" />
            </el-select>
            <el-select 
              v-else
              v-model="formData.item_id" 
              placeholder="请选择门店" 
              filterable
              style="width: 100%"
            >
              <el-option v-for="store in storeOptions" :key="store.id" :label="store.store_name" :value="store.id" />
            </el-select>
          </el-form-item>
  
          <el-form-item label="封面图">
            <el-upload
              :file-list="coverFiles"
              action="#"
              :limit="1"
              accept="image/*"
              :auto-upload="false"
              :on-change="handleCoverChange"
              :on-remove="() => formData.cover_image = ''"
              list-type="picture-card"
            >
              <el-icon><Plus /></el-icon>
              <template #tip>
                <div class="upload-tip">建议尺寸 400x200px</div>
              </template>
            </el-upload>
          </el-form-item>
  
          <el-form-item label="排序">
            <el-input-number v-model="formData.sort_order" :min="0" :max="999" />
          </el-form-item>
  
          <el-form-item label="状态">
            <el-switch v-model="formData.status" :active-value="1" :inactive-value="0" active-text="显示" inactive-text="隐藏" />
          </el-form-item>
        </el-form>
  
        <template #footer>
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="submitForm" :loading="submitting">确 定</el-button>
        </template>
      </el-dialog>
    </div>
  </template>
  
  <script>
  import { ref, reactive, onMounted } from 'vue'
  import { ElMessage, ElLoading } from 'element-plus'
  import Cookies from 'js-cookie'
  import axios from 'axios'
  import config from '@/config'
  import { getUploadSignature, uploadToOSS } from '@/utils/uploadUtils'
  import { Plus, Edit, Delete } from '@element-plus/icons-vue'
  
  export default {
    name: 'HomeRecommendPage',
    components: { Plus, Edit, Delete },
    setup() {
      const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
      const agentId = ref(userInfo.agent_id || 0)
      const storeId = ref(userInfo.id)
      const defaultImage = 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png'
  
      const loading = ref(false)
      const submitting = ref(false)
      const searchKey = ref('')
      const sectionTypeFilter = ref(null)
      const statusFilter = ref(null)
      const recommendList = ref([])
      const productOptions = ref([])
      const activityOptions = ref([])
      const storeOptions = ref([])
  
      const dialogVisible = ref(false)
      const isEditMode = ref(false)
      const formRef = ref(null)
      const coverFiles = ref([])
  
      const formData = reactive({
        id: null,
        section_name: '',
        section_type: 'product',
        section_title: '',
        section_subtitle: '',
        item_id: null,
        cover_image: '',
        sort_order: 0,
        status: 1
      })
  
      const formRules = {
        section_name: [{ required: true, message: '请输入区块名称', trigger: 'blur' }],
        section_type: [{ required: true, message: '请选择推荐类型', trigger: 'change' }],
        item_id: [{ required: true, message: '请选择推荐内容', trigger: 'change' }]
      }
  
      const pagination = reactive({
        page: 1,
        limit: 12,
        total: 0
      })
  
      const getTypeText = (type) => {
        const map = { product: '商品', activity: '活动', merchant: '商户' }
        return map[type] || type
      }
  
      const getTypeClass = (type) => {
        const map = { product: 'type-product', activity: 'type-activity', merchant: 'type-merchant' }
        return map[type] || ''
      }
  
      const onTypeChange = () => {
        formData.item_id = null
      }
  
      // 上传图片函数
      const uploadImage = async (file) => {
        if (!file.type.startsWith('image/')) {
          ElMessage.error('只能上传图片文件')
          return null
        }
        if (file.size / 1024 / 1024 > 5) {
          ElMessage.error('图片大小不能超过 5MB')
          return null
        }
  
        try {
          const signature = await getUploadSignature('image')
          const ossRes = await uploadToOSS(file, signature)
          return ossRes.url
        } catch (error) {
          console.error('上传失败:', error)
          ElMessage.error('上传失败')
          return null
        }
      }
  
      // 封面图上传处理
      const handleCoverChange = async (file) => {
        if (!file.raw) return
        const url = await uploadImage(file.raw)
        if (url) {
          formData.cover_image = url
          coverFiles.value = [{ uid: file.uid, name: file.name, url }]
          ElMessage.success('上传成功')
        }
      }
  
      // 获取商品列表
      const fetchProducts = async () => {
        try {
          const response = await axios.get(`${config.BASE_URL}/shop/api/product_api.php`, {
            params: { agent_id: agentId.value, store_id: storeId.value, limit: 100 }
          })
          if (response.data.code === 200) {
            productOptions.value = response.data.data.list
          }
        } catch (error) {
          console.error('获取商品失败:', error)
        }
      }
  
      // 获取活动列表
      const fetchActivities = async () => {
        try {
          const response = await axios.get(`${config.BASE_URL}/shop/api/activity_api.php`, {
            params: { action: 'activities', agent_id: agentId.value, store_id: storeId.value, limit: 100 }
          })
          if (response.data.code === 200) {
            activityOptions.value = response.data.data.list
          }
        } catch (error) {
          console.error('获取活动失败:', error)
        }
      }
  
      // 获取门店列表
      const fetchStores = async () => {
        try {
          const response = await axios.get(`${config.BASE_URL}/shop/api/group_store_api.php`, {
            params: { agent_id: agentId.value, store_id: storeId.value, limit: 100 }
          })
          if (response.data.code === 200) {
            storeOptions.value = response.data.data.list
          }
        } catch (error) {
          console.error('获取门店失败:', error)
        }
      }
  
      // 获取推荐列表
      const fetchData = async () => {
        loading.value = true
        try {
          const params = {
            agent_id: agentId.value,
            store_id: storeId.value,
            page: pagination.page,
            limit: pagination.limit,
            search: searchKey.value,
            section_type: sectionTypeFilter.value,
            status: statusFilter.value
          }
  
          const response = await axios.get(`${config.BASE_URL}/shop/api/home_recommend_api.php`, { params })
  
          if (response.data.code === 200) {
            recommendList.value = response.data.data.list
            pagination.total = response.data.data.pagination.total
          }
        } catch (error) {
          console.error('获取推荐列表失败:', error)
          ElMessage.error('获取数据失败')
        } finally {
          loading.value = false
        }
      }
  
      // 重置搜索
      const resetSearch = () => {
        searchKey.value = ''
        sectionTypeFilter.value = null
        statusFilter.value = null
        pagination.page = 1
        fetchData()
      }
  
      // 打开新增弹窗
      const openAddDialog = () => {
        isEditMode.value = false
        coverFiles.value = []
        Object.assign(formData, {
          id: null,
          section_name: '',
          section_type: 'product',
          section_title: '',
          section_subtitle: '',
          item_id: null,
          cover_image: '',
          sort_order: 0,
          status: 1
        })
        dialogVisible.value = true
      }
  
      // 打开编辑弹窗
      const openEditDialog = (row) => {
        isEditMode.value = true
        Object.assign(formData, row)
        
        if (row.cover_image) {
          coverFiles.value = [{ uid: Date.now(), name: 'cover', url: row.cover_image }]
        }
        
        dialogVisible.value = true
      }
  
      // 提交表单
      const submitForm = async () => {
        if (!formRef.value) return
  
        await formRef.value.validate(async (valid) => {
          if (!valid) return
  
          submitting.value = true
          const loadingInstance = ElLoading.service({ text: '保存中...' })
  
          try {
            const payload = {
              agent_id: agentId.value,
              store_id: storeId.value,
              ...formData
            }
  
            let response
            if (isEditMode.value) {
              payload.id = formData.id
              response = await axios.put(`${config.BASE_URL}/shop/api/home_recommend_api.php`, payload)
            } else {
              response = await axios.post(`${config.BASE_URL}/shop/api/home_recommend_api.php`, payload)
            }
  
            if (response.data.code === 200) {
              ElMessage.success(isEditMode.value ? '更新成功' : '创建成功')
              dialogVisible.value = false
              fetchData()
            } else {
              ElMessage.error(response.data.message || '操作失败')
            }
          } catch (error) {
            console.error('提交失败:', error)
            ElMessage.error('操作失败')
          } finally {
            submitting.value = false
            loadingInstance.close()
          }
        })
      }
  
      // 删除
      const handleDelete = async (id) => {
        const loadingInstance = ElLoading.service({ text: '删除中...' })
        try {
          const response = await axios.delete(`${config.BASE_URL}/shop/api/home_recommend_api.php`, {
            data: { id, store_id: storeId.value }
          })
          if (response.data.code === 200) {
            ElMessage.success('删除成功')
            fetchData()
          } else {
            ElMessage.error(response.data.message || '删除失败')
          }
        } catch (error) {
          console.error('删除失败:', error)
          ElMessage.error('删除失败')
        } finally {
          loadingInstance.close()
        }
      }
  
      onMounted(() => {
        fetchProducts()
        fetchActivities()
        fetchStores()
        fetchData()
      })
  
      return {
        loading,
        submitting,
        searchKey,
        sectionTypeFilter,
        statusFilter,
        recommendList,
        productOptions,
        activityOptions,
        storeOptions,
        pagination,
        defaultImage,
        dialogVisible,
        isEditMode,
        formRef,
        formData,
        formRules,
        coverFiles,
        getTypeText,
        getTypeClass,
        onTypeChange,
        handleCoverChange,
        resetSearch,
        openAddDialog,
        openEditDialog,
        submitForm,
        handleDelete,
        fetchData
      }
    }
  }
  </script>
  
  <style scoped lang="scss">
  .home-recommend {
    .page-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
      
      .page-title {
        font-size: 24px;
        font-weight: 600;
        color: #1a1a2e;
        margin-bottom: 4px;
      }
      
      .page-desc {
        font-size: 14px;
        color: #8c8c8c;
      }
    }
    
    .filter-bar {
      display: flex;
      gap: 12px;
      flex-wrap: wrap;
      margin-bottom: 24px;
    }
    
    .recommend-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
      gap: 20px;
      
      .recommend-card {
        background: #fff;
        border-radius: 16px;
        overflow: hidden;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        transition: all 0.3s;
        
        &:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
        }
        
        .recommend-cover {
          position: relative;
          height: 160px;
          overflow: hidden;
          
          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
          
          .recommend-type {
            position: absolute;
            top: 12px;
            right: 12px;
            padding: 4px 12px;
            border-radius: 20px;
            font-size: 12px;
            font-weight: 500;
            
            &.type-product {
              background: rgba(64, 158, 255, 0.9);
              color: #fff;
            }
            
            &.type-activity {
              background: rgba(230, 162, 60, 0.9);
              color: #fff;
            }
            
            &.type-merchant {
              background: rgba(103, 194, 58, 0.9);
              color: #fff;
            }
          }
        }
        
        .recommend-info {
          padding: 16px;
          
          .section-name {
            font-size: 16px;
            font-weight: 600;
            margin-bottom: 8px;
          }
          
          .section-title {
            font-size: 14px;
            color: #666;
            margin-bottom: 4px;
          }
          
          .section-subtitle {
            font-size: 12px;
            color: #8c8c8c;
            margin-bottom: 8px;
          }
          
          .recommend-meta {
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 12px;
            color: #8c8c8c;
          }
        }
        
        .recommend-actions {
          padding: 12px 16px;
          border-top: 1px solid #f0f0f0;
          display: flex;
          gap: 16px;
        }
      }
    }
    
    .empty-state {
      grid-column: 1 / -1;
      padding: 60px 0;
    }
    
    .pagination-wrapper {
      display: flex;
      justify-content: flex-end;
      margin-top: 24px;
    }
    
    .upload-tip {
      font-size: 12px;
      color: #8c8c8c;
      margin-top: 8px;
    }
  }
  </style>